'use client';
import {
	TopLevelKeys,
	Properties as PropertiesState,
	NestedKeys,
	PropertiesContextType,
} from '@/types/properties';
import React, {
	createContext,
	useContext,
	useState,
	ReactNode,
	useEffect,
} from 'react';

export const defaultValues: PropertiesState = {
	query: '',
	location: {
		name: '',
		lat: 0,
		lng: 0,
	},
	injury: '',
	severity: '',
	time: '',
	treatments: {
		acupuncture: true,
		brain: true,
		brain_stimulation: true,
		equipment: true,
		pharma: true,
		estimulation: true,
		epidural: true,
		exo: true,
		fes: true,
		intermittent_hypoxia: true,
		nmes: true,
		nutrition: true,
		observational: true,
		rehabilitation: true,
		safety: true,
		stemcells: true,
		surgery: true,
		tdcs: true,
		tms: true,
		transcutaneous: true,
	},
	outcomes: {
		arms: true,
		bladder: true,
		blood_pressure: true,
		bones: true,
		bowels: true,
		breathing: true,
		hands: true,
		legs: true,
		motor: true,
		mental_health: true,
		neuroprotection: true,
		pain: true,
		plasticity: true,
		pressure: true,
		quality_of_life: true,
		regeneration: true,
		sensation: true,
		sex: true,
		spasticity: true,
		temperature: true,
		trunk: true,
		walking: true,
	},
	markedTrials: {
		followedByYou: false,
		hiddenByYou: false,
	},
	trialStatus: {
		RECRUITING: true,
		ENROLLING_BY_INVITATION: true,
		ACTIVE_NOT_RECRUITING: true,
		NOT_YET_RECRUITING: true,
		UNKNOWN: false,
	},
	other: {
		reviewed: true,
		not_reviewed: false,
	},
	trialsPerPage: 10,
	sortBy: 'lastUpdate',
	page: 1,
};

const PropertiesContext = createContext<PropertiesContextType | undefined>(
	undefined
);

export const PropertiesProvider = ({ children }: { children: ReactNode }) => {
	const [properties, setProperties] = useState<PropertiesState>(defaultValues);

	const updateProperty = (
		key: TopLevelKeys,
		value: string | { name: string; lat: number; lng: number } | number
	) => {
		setProperties((prev) => ({
			...prev,
			[key]: value,
			page: key === 'page' ? (value as number) : 1,
		}));
	};

	const updateNestedProperty = (
		section: NestedKeys,
		key: string,
		value: boolean | number
	) => {
		setProperties((prev) => ({
			...prev,
			[section]: {
				...prev[section],
				[key]: value,
			},
			page: 1,
		}));
	};

	const getFilteredProperties = () => {
		const {
			query,
			injury,
			time,
			severity,
			location,
			sortBy,
			trialsPerPage,
			page,
			treatments: treatmentFilters,
			outcomes: outcomeFilters,
			markedTrials,
			trialStatus,
			other,
		} = properties;

		const filtered: Partial<PropertiesState> = {};

		if (query.trim()) filtered.query = query;
		if (injury.trim()) filtered.injury = injury;
		if (time.trim()) filtered.time = time;
		if (severity.trim()) filtered.severity = severity;

		if (location.lat !== 0 && location.lng !== 0) {
			filtered.location = location;
		}

		filtered.sortBy = sortBy;
		filtered.trialsPerPage = trialsPerPage;
		filtered.page = page;

		const filterTruthy = <T extends Record<string, boolean>>(section: T) => {
			return Object.fromEntries(
				Object.entries(section).filter(([_, v]) => v === true)
			) as T;
		};

		const filteredTreatments = filterTruthy(treatmentFilters);
		if (Object.keys(filteredTreatments).length > 0) {
			filtered.treatments = filteredTreatments;
		}

		const filteredOutcomes = filterTruthy(outcomeFilters);
		if (Object.keys(filteredOutcomes).length > 0) {
			filtered.outcomes = filteredOutcomes;
		}

		const filteredMarked = filterTruthy(markedTrials);
		if (Object.keys(filteredMarked).length > 0) {
			filtered.markedTrials = filteredMarked;
		}

		const filteredStatus = filterTruthy(trialStatus);
		if (Object.keys(filteredStatus).length > 0) {
			filtered.trialStatus = filteredStatus;
		}

		const filteredOther = filterTruthy(other);
		if (Object.keys(filteredOther).length > 0) {
			filtered.other = filteredOther;
		}

		return filtered;
	};

	useEffect(() => {
		const saved = localStorage.getItem('Properties');
		if (saved) {
			try {
				const parsed = JSON.parse(saved);
				if (parsed.expiry && Date.now() > parsed.expiry) {
					localStorage.removeItem('Properties');
				} else {
					const { expiry, ...filtered } = parsed;
					setProperties({ ...defaultValues, ...filtered });
				}
			} catch (err) {
				console.error('Failed to parse saved properties:', err);
			}
		}
	}, []);

	useEffect(() => {
		const filtered = getFilteredProperties();
		const expiry = Date.now() + 3600000; // 1 hour from now
		localStorage.setItem('Properties', JSON.stringify({ ...filtered, expiry }));
	}, [properties]);

	return (
		<PropertiesContext.Provider
			value={{
				properties,
				updateProperty,
				updateNestedProperty,
			}}
		>
			{children}
		</PropertiesContext.Provider>
	);
};

export const useProperties = () => {
	const context = useContext(PropertiesContext);
	if (!context) {
		throw new Error('useProperties must be used within a PropertiesProvider');
	}
	return context;
};
